import React, { useCallback, useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import CharacterCount from '@tiptap/extension-character-count';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import { Button } from './button';
import { 
  Bold, 
  Italic, 
  Underline as UnderlineIcon, 
  List, 
  ListOrdered, 
  AlignLeft, 
  AlignCenter, 
  AlignRight, 
  Link as LinkIcon,
  Highlighter,
  Palette,
  TableIcon,
  Plus,
  Minus,
  Trash2
} from 'lucide-react';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from './select';
import { findTextInContent } from '@/utils/textMatching';
import { Mark, mergeAttributes } from '@tiptap/core';
import { cleanWavyUnderlines } from '@/utils/cleanWavyUnderlines';
import { TextSelection } from 'prosemirror-state';
import { htmlToPlainText } from '@/utils/htmlToPlainText';

// WavyUnderline extension for highlighting text with wavy underline
const WavyUnderline = Mark.create({
  name: 'wavyUnderline',

  addOptions() {
    return {
      HTMLAttributes: {},
    }
  },

  addAttributes() {
    return {
      color: {
        default: '#FF7A09',
        parseHTML: element => element.getAttribute('data-color'),
        renderHTML: attributes => {
          if (!attributes.color) {
            return {}
          }
          return {
            'data-color': attributes.color,
          }
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-wavy-underline]',
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    const color = HTMLAttributes['data-color'] || '#FF7A09'
    return [
      'span',
      mergeAttributes(
        {
          'data-wavy-underline': '',
          'data-color': color,
          style: `text-decoration: underline; text-decoration-style: wavy; text-decoration-color: ${color}; text-underline-offset: 2px;`,
        },
        this.options.HTMLAttributes,
        HTMLAttributes,
      ),
      0,
    ]
  },

  addCommands() {
    return {
      setWavyUnderline:
        (attributes) =>
        ({ commands }) => {
          return commands.setMark(this.name, attributes)
        },
      toggleWavyUnderline:
        (attributes) =>
        ({ commands }) => {
          return commands.toggleMark(this.name, attributes)
        },
      unsetWavyUnderline:
        () =>
        ({ commands }) => {
          return commands.unsetMark(this.name)
        },
    }
  },
})

// Extend the commands interface
declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    wavyUnderline: {
      setWavyUnderline: (attributes?: { color?: string }) => ReturnType
      toggleWavyUnderline: (attributes?: { color?: string }) => ReturnType
      unsetWavyUnderline: () => ReturnType
    }
  }
}

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  maxLength?: number;
  highlightedText?: string; // Text to highlight from AI response
  highlightColor?: string; // Color for the wavy underline highlight
  onHighlightedTextChange?: (text: string | null) => void; // Callback when highlighted text changes
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = "Start writing...",
  disabled = false,
  maxLength,
  highlightedText,
  highlightColor = '#FF7A09',
  onHighlightedTextChange
}) => {

  // Handler to show warning if user tries to insert an image
  const [showImageWarning, setShowImageWarning] = React.useState(false);
  const previousHighlightedText = useRef<string | undefined>(undefined);
  const isApplyingHighlight = useRef(false);
  
  // State to track if editor is focused (cursor is active)
  const [isFocused, setIsFocused] = React.useState(false);
  const [isHoveringToolbar, setIsHoveringToolbar] = React.useState(false);
  const [isSelectOpen, setIsSelectOpen] = React.useState(false);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 underline cursor-pointer',
        },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Underline,
      TextStyle,
      Color,
      WavyUnderline,
      CharacterCount,
      Table.configure({
        resizable: true,
        HTMLAttributes: {
          class: 'border-collapse table-auto w-full',
        },
      }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor }) => {
      // Don't trigger onChange when we're just applying highlights
      if (isApplyingHighlight.current) {
        return;
      }
      const html = editor.getHTML();
      onChange(html);
    },
    onFocus: ({ editor }) => {
      // Show toolbar when editor gains focus (cursor is active)
      setIsFocused(true);
      // Clear any pending hide timeout
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
    },
    onBlur: ({ editor }) => {
      // Delay hiding toolbar to allow interaction with toolbar buttons
      hideTimeoutRef.current = setTimeout(() => {
        if (!isHoveringToolbar && !isSelectOpen) {
          setIsFocused(false);
        }
      }, 150); // 150ms delay
    },
    onCreate: ({ editor }) => {
      // Editor created
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[120px] p-3',
      },
    },
  });

  const removeAllWavyUnderlines = useCallback(() => {
    if (!editor) {
      return;
    }

    const { state, view } = editor;
    const markType = state.schema.marks.wavyUnderline;

    if (!markType) {
      return;
    }

    let transaction = state.tr;
    let markRemoved = false;

    state.doc.descendants((node, pos) => {
      if (!node.isText) {
        return;
      }

      if (!markType.isInSet(node.marks)) {
        return;
      }

      const textLength = node.text?.length ?? 0;

      if (textLength === 0) {
        return;
      }

      transaction = transaction.removeMark(pos, pos + textLength, markType);
      markRemoved = true;
    });

    if (!markRemoved) {
      return;
    }

    try {
      transaction = transaction.setSelection(state.selection);
    } catch (error) {
      transaction = transaction.setSelection(TextSelection.atEnd(transaction.doc));
    }
    view.dispatch(transaction);
  }, [editor]);

  const restoreSelection = useCallback(
    (from: number, to: number) => {
      if (!editor) {
        return;
      }

      const docSize = editor.state.doc.content.size;

      if (docSize === 0) {
        return;
      }

      const safeFrom = Math.max(0, Math.min(from, docSize));
      const safeTo = Math.max(safeFrom, Math.min(to, docSize));

      try {
        editor.commands.setTextSelection({ from: safeFrom, to: safeTo });
      } catch (error) {
        try {
          const end = editor.state.doc.content.size;
          editor.commands.setTextSelection({ from: end, to: end });
        } catch {
          // If we still fail, ignore—better to leave selection unset than throw
        }
      }
    },
    [editor]
  );

  const scheduleRestoreSelection = useCallback(
    (from: number, to: number) => {
      if (!editor) {
        return;
      }

      if (typeof window !== "undefined" && window.requestAnimationFrame) {
        window.requestAnimationFrame(() => restoreSelection(from, to));
      } else {
        restoreSelection(from, to);
      }
    },
    [editor, restoreSelection]
  );

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      // Get current content without wavy underlines
      const currentContentClean = cleanWavyUnderlines(editor.getHTML());
      const newContentClean = cleanWavyUnderlines(value);

      // Only update if the actual content (without highlights) has changed
      if (currentContentClean !== newContentClean) {
        editor.commands.setContent(value, false);
      }
    }
  }, [value, editor]);

  // Update editor editable state when disabled prop changes
  // This fixes the bug where sections become non-editable after returning from preview mode
  useEffect(() => {
    if (editor) {
      const shouldBeEditable = !disabled;
      if (editor.isEditable !== shouldBeEditable) {
        editor.setEditable(shouldBeEditable);
      }
    }
  }, [editor, disabled]);

  // Add click outside detection to hide toolbar
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (editor && !editor.isFocused) {
        setIsFocused(false);
      }
    };

    document.addEventListener('click', handleClick);
    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [editor]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  // Prevent image paste and drop
  useEffect(() => {
    if (!editor) return;
    // Prevent image paste
    const handlePaste = (event: ClipboardEvent) => {
      if (event.clipboardData && Array.from(event.clipboardData.items).some(item => item.type.startsWith('image/'))) {
        event.preventDefault();
        setShowImageWarning(true);
      }
    };
    // Prevent image drop
    const handleDrop = (event: DragEvent) => {
      if (event.dataTransfer && Array.from(event.dataTransfer.items).some(item => item.type.startsWith('image/'))) {
        event.preventDefault();
        setShowImageWarning(true);
      }
    };
    const dom = editor.view.dom;
    dom.addEventListener('paste', handlePaste);
    dom.addEventListener('drop', handleDrop);
    return () => {
      dom.removeEventListener('paste', handlePaste);
      dom.removeEventListener('drop', handleDrop);
    };
  }, [editor]);

  // Auto-hide the warning after a few seconds
  useEffect(() => {
    if (showImageWarning) {
      const timeout = setTimeout(() => setShowImageWarning(false), 3000);
      return () => clearTimeout(timeout);
    }
  }, [showImageWarning]);

  // Handle text highlighting based on AI response
  useEffect(() => {
    if (!editor) return;

    // Clear previous wavy underlines if highlighted text changed
    if (previousHighlightedText.current !== highlightedText) {
      removeAllWavyUnderlines();
      previousHighlightedText.current = highlightedText;
    }

    const normalizedHighlight = highlightedText ? htmlToPlainText(highlightedText) : "";

    if (!normalizedHighlight) {
      const currentHtml = editor.getHTML();
      const cleanedHtml = cleanWavyUnderlines(currentHtml);
      if (cleanedHtml !== currentHtml) {
        const { from, to } = editor.state.selection;
        editor.commands.setContent(cleanedHtml, true);
        scheduleRestoreSelection(from, to);
      }
      return;
    }

    // Find the text in the editor content
    const content = editor.getText();
    const match = findTextInContent(content, normalizedHighlight);

    if (!match.found) {
      console.warn('❌ Text not found:', {
        search: normalizedHighlight.substring(0, 60) + '...',
        editorHasContent: content.length > 0,
        contentStart: content.substring(0, 100)
      });
    }

    if (match.found) {
      // Apply wavy underline to the matched text
      const docSize = editor.state.doc.content.size;
      const safeFrom = Math.max(0, Math.min(match.startIndex, docSize));
      const safeTo = Math.max(safeFrom, Math.min(match.endIndex, docSize));

      console.log('✅ Underline applied:', { from: safeFrom, to: safeTo });

      // Set flag to prevent onUpdate from firing
      isApplyingHighlight.current = true;

      editor
        .chain()
        .setTextSelection({ from: safeFrom, to: safeTo })
        .setWavyUnderline({ color: highlightColor }) // Dynamic wavy underline color
        .run();

      // Reset flag after a brief delay
      setTimeout(() => {
        isApplyingHighlight.current = false;
      }, 100);

      // Notify parent component about the highlighted text
      if (onHighlightedTextChange) {
        onHighlightedTextChange(normalizedHighlight);
      }
    } else {
      // Text not found, notify parent
      if (onHighlightedTextChange) {
        onHighlightedTextChange(null);
      }
    }
  }, [editor, highlightedText, highlightColor, onHighlightedTextChange, removeAllWavyUnderlines, restoreSelection, scheduleRestoreSelection]);


  if (!editor) {
    return null;
  }

  const addLink = () => {
    const url = window.prompt('Enter URL');
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  const setTextAlign = (align: 'left' | 'center' | 'right') => {
    editor.chain().focus().setTextAlign(align).run();
  };

  const setColor = (color: string) => {
    editor.chain().focus().setColor(color).run();
  };

  const setHighlight = (color: string) => {
    editor.chain().focus().setHighlight({ color }).run();
  };


  return (
    <div className="border rounded-md bg-background flex flex-col">
      {showImageWarning && (
        <div className="bg-yellow-100 text-yellow-800 px-3 py-2 text-xs border-b border-yellow-300">
          Image uploads are disabled in this editor.
        </div>
      )}
      {/* Toolbar at the top - show when editor is focused (cursor is active) */}
      {(isFocused || isHoveringToolbar || isSelectOpen) && (
        <div 
          className="border-b p-2 flex flex-wrap gap-1 bg-white items-center sticky top-0 z-10" 
          aria-label="Text formatting toolbar"
          onMouseEnter={() => {
            setIsHoveringToolbar(true);
            if (hideTimeoutRef.current) {
              clearTimeout(hideTimeoutRef.current);
            }
          }}
          onMouseLeave={() => {
            setIsHoveringToolbar(false);
            if (!editor?.isFocused) {
              hideTimeoutRef.current = setTimeout(() => {
                setIsFocused(false);
              }, 150);
            }
          }}
        >
        {/* Heading Dropdown */}
        <Select
          value={
            editor.isActive('heading', { level: 1 }) ? 'h1' :
            editor.isActive('heading', { level: 2 }) ? 'h2' :
            editor.isActive('heading', { level: 3 }) ? 'h3' :
            editor.isActive('heading', { level: 4 }) ? 'h4' :
            'paragraph'
          }
          onValueChange={(value) => {
            if (value === 'paragraph') {
              editor.chain().focus().setParagraph().run();
            } else {
              const level = parseInt(value.replace('h', ''));
              editor.chain().focus().toggleHeading({ level: level as 1 | 2 | 3 | 4 | 5 | 6 }).run();
            }
            setIsSelectOpen(false);
          }}
          onOpenChange={(open) => {
            setIsSelectOpen(open);
            if (open) {
              // Clear any pending hide timeout when dropdown opens
              if (hideTimeoutRef.current) {
                clearTimeout(hideTimeoutRef.current);
                hideTimeoutRef.current = null;
              }
            }
          }}
        >
          <SelectTrigger className="w-32 h-8 text-xs">
            <SelectValue placeholder="Text style" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="paragraph">Normal text</SelectItem>
            <SelectItem value="h1">Heading 1</SelectItem>
            <SelectItem value="h2">Heading 2</SelectItem>
            <SelectItem value="h3">Heading 3</SelectItem>
            <SelectItem value="h4">Heading 4</SelectItem>
          </SelectContent>
        </Select>
        {/* End Heading Dropdown */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={!editor.can().chain().focus().toggleBold().run()}
          className={editor.isActive('bold') ? 'bg-muted' : ''}
        >
          <Bold className="h-4 w-4" />
        </Button>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={!editor.can().chain().focus().toggleItalic().run()}
          className={editor.isActive('italic') ? 'bg-muted' : ''}
        >
          <Italic className="h-4 w-4" />
        </Button>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={editor.isActive('underline') ? 'bg-muted' : ''}
        >
          <UnderlineIcon className="h-4 w-4" />
        </Button>

        <div className="w-px h-6 bg-border mx-1" />

        <Button
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={editor.isActive('bulletList') ? 'bg-muted' : ''}
        >
          <List className="h-4 w-4" />
        </Button>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={editor.isActive('orderedList') ? 'bg-muted' : ''}
        >
          <ListOrdered className="h-4 w-4" />
        </Button>

        <div className="w-px h-6 bg-border mx-1" />

        <Button
          variant="ghost"
          size="sm"
          onClick={() => setTextAlign('left')}
          className={editor.isActive({ textAlign: 'left' }) ? 'bg-muted' : ''}
        >
          <AlignLeft className="h-4 w-4" />
        </Button>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setTextAlign('center')}
          className={editor.isActive({ textAlign: 'center' }) ? 'bg-muted' : ''}
        >
          <AlignCenter className="h-4 w-4" />
        </Button>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setTextAlign('right')}
          className={editor.isActive({ textAlign: 'right' }) ? 'bg-muted' : ''}
        >
          <AlignRight className="h-4 w-4" />
        </Button>

        <div className="w-px h-6 bg-border mx-1" />

        <Button
          variant="ghost"
          size="sm"
          onClick={addLink}
          className={editor.isActive('link') ? 'bg-muted' : ''}
        >
          <LinkIcon className="h-4 w-4" />
        </Button>

        <Button
          variant="ghost"
          size="sm"
          onClick={() => setColor('#ef4444')}
          className="text-red-500"
        >
          <Palette className="h-4 w-4" />
        </Button>

        <Button
          variant="ghost"
          size="sm"
          onClick={() => setHighlight('#fef3c7')}
        >
          <Highlighter className="h-4 w-4" />
        </Button>

        <div className="w-px h-6 bg-border mx-1" />

        {/* Table Controls */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
          title="Insert Table"
        >
          <TableIcon className="h-4 w-4" />
        </Button>

        {editor.isActive('table') && (
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => editor.chain().focus().addColumnAfter().run()}
              title="Add Column"
            >
              <Plus className="h-3 w-3" />
              <span className="text-xs ml-0.5">Col</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => editor.chain().focus().addRowAfter().run()}
              title="Add Row"
            >
              <Plus className="h-3 w-3" />
              <span className="text-xs ml-0.5">Row</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => editor.chain().focus().deleteColumn().run()}
              title="Delete Column"
            >
              <Minus className="h-3 w-3" />
              <span className="text-xs ml-0.5">Col</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => editor.chain().focus().deleteRow().run()}
              title="Delete Row"
            >
              <Minus className="h-3 w-3" />
              <span className="text-xs ml-0.5">Row</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => editor.chain().focus().deleteTable().run()}
              title="Delete Table"
              className="text-red-500 hover:text-red-600"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </>
        )}
        </div>
      )}
      {/* Editor Content with resize handle similar to textarea */}
      <div className="min-h-[120px] overflow-auto resize-y rounded-b-md">
        <EditorContent 
          editor={editor} 
          className="prose prose-sm max-w-none focus:outline-none min-h-[120px] p-3 [&_.ProseMirror]:min-h-[120px] [&_.ProseMirror]:outline-none [&_.ProseMirror]:border-none [&_.ProseMirror]:bg-transparent [&_.ProseMirror]:p-0 [&_.ProseMirror]:text-sm [&_.ProseMirror]:leading-relaxed [&_table]:w-full [&_table]:border-collapse [&_table]:my-4 [&_th]:border [&_th]:border-gray-300 [&_th]:bg-gray-100 [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-semibold [&_th]:text-sm [&_td]:border [&_td]:border-gray-300 [&_td]:px-3 [&_td]:py-2 [&_td]:text-sm"
        />
      </div>
      {/* Character Count */}
      {maxLength && (
        <div className="border-t p-2 text-xs text-muted-foreground bg-muted/20">
          {editor.storage.characterCount?.characters() || 0}/{maxLength} characters
        </div>
      )}
    </div>
  );
};

export { RichTextEditor }; 
